import {
  AXIS_FONT_SIZE_PX,
  PRICE_AXIS_PADDING_X_PX,
  PRICE_AXIS_MIN_WIDTH_PX,
  PRICE_BADGE_ARROW_WIDTH_PX,
} from "../constants";
import { axisFont } from "../text/axisFont";
import { formatPrice } from "./priceFormat";

/**
 * As in PO (spec §12.2): a grid of whole prices is labelled without decimals
 * (80,300), otherwise labels keep the pair precision (1.10200).
 *
 * @param {Array<{ price: number }>} ticks
 * @param {number} decimals
 * @returns {boolean}
 */
const isWholeGrid = (ticks, decimals) => {
  const tolerance = 0.5 * 10 ** -decimals;

  return ticks.length > 0 && ticks.every(({ price }) => Math.abs(price - Math.round(price)) < tolerance);
};

/**
 * Price labels with their measured widths.
 *
 * @param {Array<{ price: number, y: number }>} ticks
 * @param {number} decimals
 * @param {(text: string, font: string) => number} measure
 * @param {string} fontFamily
 * @returns {Array<{ y: number, text: string, width: number }>}
 */
export const buildPriceLabels = (ticks, decimals, measure, fontFamily) => {
  const font = axisFont(fontFamily);
  const labelDecimals = isWholeGrid(ticks, decimals) ? 0 : decimals;

  return ticks.map(({ price, y }) => {
    const text = formatPrice(price, labelDecimals);

    return { y, text, width: measure(text, font) };
  });
};

/**
 * Width of a price at the full pair precision (E9: the crosshair label and
 * badges), measured at the top of the pane, where prices are the longest.
 *
 * @param {object} priceScale
 * @param {number} decimals
 * @param {(text: string, font: string) => number} measure
 * @param {string} fontFamily
 * @returns {number}
 */
export const fullPriceWidth = (priceScale, decimals, measure, fontFamily) =>
  measure(formatPrice(priceScale.yToPrice(0), decimals), axisFont(fontFamily));

/**
 * Width of the price axis: the longest label plus padding and the point of
 * the price badges (E16).
 *
 * @param {Array<{ width: number }>} labels
 * @param {number} [minLabelWidth] - e.g. fullPriceWidth, so the crosshair label fits
 * @returns {number}
 */
export const priceAxisWidthFor = (labels, minLabelWidth = 0) => {
  const longest = labels.reduce((max, label) => Math.max(max, label.width), minLabelWidth);

  return Math.max(
    PRICE_AXIS_MIN_WIDTH_PX,
    Math.ceil(longest) + PRICE_AXIS_PADDING_X_PX * 2 + PRICE_BADGE_ARROW_WIDTH_PX,
  );
};

/**
 * Draws price labels to the right of the pane, in line with the text of the
 * price badges, skipping the ones that would be cut by the pane edges.
 *
 * @param {object} textLayer
 * @param {Array<{ y: number, text: string }>} labels
 * @param {{ width: number, height: number, top?: number }} pane - top: of an oscillator pane in the chart
 * @param {{ colors: object, fontFamily: string }} theme
 */
export const drawPriceAxis = (textLayer, labels, pane, theme) => {
  const top = pane.top || 0;
  const style = { font: axisFont(theme.fontFamily), color: theme.colors.axisText.css, align: "left" };
  const halfText = AXIS_FONT_SIZE_PX / 2;
  const x = pane.width + PRICE_BADGE_ARROW_WIDTH_PX + PRICE_AXIS_PADDING_X_PX;

  labels.forEach(({ y, text }) => {
    if (y >= top + halfText && y <= top + pane.height - halfText) textLayer.drawText(text, x, y, style);
  });
};
